import { Stack, Typography, Box, Skeleton } from "@mui/material";
import { TrendCategory } from "app/containers/TeamsTab/types";
import {
  trendCategoryLabel,
  trendCategorySemanticColor,
  TrendCategorySemanticColor,
} from "app/containers/TeamsTab/helpers";

export type EmployeeSummaryPrepCardProps = {
  content: string;
  energyTrend?: (number | null)[] | null;
  /** Mood trend classification, from CompletedCheckInSummary.trendDirection.mood. */
  moodTrend?: TrendCategory | null;
  isSummaryLoading?: boolean;
};

/** Returns the CSS variable string for a score's bar color (for SVG fill use). */
const getBarColorCssVar = (v: number | null): string => {
  if (!v) return "var(--team-app-bar-empty)";
  if (v < 2) return "var(--team-app-score-critical)";
  if (v < 4) return "var(--team-app-score-medium)";
  return "var(--team-app-score-high)";
};

const semanticColorClass: Record<TrendCategorySemanticColor, string> = {
  success: "text-teamApp-trend-improving",
  error: "text-teamApp-trend-declining",
  purple: "text-purple-500",
  neutral: "text-teamApp-text-secondary",
};

const trendDisplay = (
  trend: TrendCategory | null | undefined
): { label: string; colorClass: string } => {
  if (!trend) {
    return {
      label: "No trend data",
      colorClass: "text-teamApp-text-secondary",
    };
  }
  return {
    label: trendCategoryLabel[trend],
    colorClass: semanticColorClass[trendCategorySemanticColor[trend]],
  };
};

const BAR_W = 14;
const BAR_GAP = 7;
const MAX_H = 44;
const MAX_SCORE = 5;
const STRIPE_SIZE = 4; // controls stripe density — smaller = tighter

const EnergyTrendBars = ({ scores }: { scores: (number | null)[] }) => {
  const totalW = scores.length * BAR_W + (scores.length - 1) * BAR_GAP;
  const uniqueColors = [...new Set(scores.map(getBarColorCssVar))];
  return (
    <svg width={totalW} height={MAX_H} viewBox={`0 0 ${totalW} ${MAX_H}`}>
      <defs>
        {uniqueColors.map((color) => (
          <pattern
            key={color}
            id={`stripe-${color.replace(/[^a-z0-9]/gi, "-")}`}
            patternUnits="userSpaceOnUse"
            width={STRIPE_SIZE}
            height={STRIPE_SIZE}
            patternTransform="rotate(45)"
          >
            <rect width={STRIPE_SIZE} height={STRIPE_SIZE} fill={color} />
            <line
              x1="0"
              y1="0"
              x2="0"
              y2={STRIPE_SIZE}
              stroke="var(--team-app-white-25)"
              strokeWidth={STRIPE_SIZE / 2}
            />
          </pattern>
        ))}
      </defs>
      {scores.map((v, i) => {
        const x = i * (BAR_W + BAR_GAP);
        const barH =
          v !== null
            ? Math.max(5, (v / MAX_SCORE) * MAX_H)
            : Math.round(MAX_H * 0.35);
        const y = MAX_H - barH;
        const color = getBarColorCssVar(v);
        return (
          <rect
            key={x}
            x={x}
            y={y}
            width={BAR_W}
            height={barH}
            fill={`url(#stripe-${color.replace(/[^a-z0-9]/gi, "-")})`}
            rx={BAR_W / 4}
          />
        );
      })}
    </svg>
  );
};

export const EmployeeSummaryPrepCard = ({
  content,
  energyTrend,
  moodTrend,
  isSummaryLoading = false,
}: EmployeeSummaryPrepCardProps) => {
  const hasTrend = energyTrend && energyTrend.length > 0;
  const { label: trendLabel, colorClass: trendColorClass } = trendDisplay(
    hasTrend ? moodTrend : null
  );

  return (
    <Stack
      paddingX="2rem"
      direction={{ xs: "column-reverse", sm: "row" }}
      gap="1rem"
      justifyContent="space-between"
      paddingY="1.5rem"
      className="bg-teamApp-surface-accent"
      sx={{ borderRadius: "1rem" }}
    >
      <Stack direction="column" justifyContent="center" rowGap=".5rem">
        <Typography
          variant="cardBodyHeader"
          className="text-navy-100"
          sx={{ fontSize: "1rem", fontWeight: 600 }}
        >
          Over Past 4 Weeks
        </Typography>
        {isSummaryLoading ? (
          <Stack rowGap=".375rem" paddingTop=".25rem">
            <Skeleton width="90%" height={14} />
            <Skeleton width="80%" height={14} />
            <Skeleton width="60%" height={14} />
          </Stack>
        ) : (
          <Typography
            variant="cardBodyRegular"
            className="text-teamApp-text-tertiary"
            sx={{
              fontSize: ".875rem",
              lineHeight: "1.125rem",
            }}
          >
            {content}
          </Typography>
        )}
      </Stack>

      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        rowGap=".25rem"
        flexShrink={0}
      >
        {hasTrend ? (
          <EnergyTrendBars scores={energyTrend} />
        ) : (
          <Box
            className="bg-teamApp-bar-empty"
            sx={{
              width: BAR_W * 4 + BAR_GAP * 3,
              height: MAX_H,
              borderRadius: ".5rem",
              opacity: 0.4,
            }}
          />
        )}
        <Typography
          className="text-teamApp-heading-dark-navy"
          sx={{ textAlign: "center" }}
          variant="h5"
        >
          Energy Trend (4wk)
        </Typography>
        <Typography
          className={trendColorClass}
          sx={{ textAlign: "center" }}
          variant="body1"
        >
          {trendLabel}
        </Typography>
      </Stack>
    </Stack>
  );
};
